import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import type { Animal } from '../types';
import { buildApiUrl } from '../config/apiConfig';
import i18n from '../config/i18n';

interface UseAnimalsReturn {
  animals: Animal[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  addAnimal: (animal: Animal) => void;
}

export function useAnimals(
  userId: number | undefined,
  token: string | undefined
): UseAnimalsReturn {
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAnimals = useCallback(async () => {
    const uid = Number(userId);
    if (!token || !Number.isFinite(uid) || uid <= 0) {
      setAnimals([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(buildApiUrl('/get-animals'), {
        params: { user_id: uid },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnimals(response.data.animals || []);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : i18n.t('common:proLegacy.loadAnimalsError');
      setError(message);
      setAnimals([]);
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => {
    fetchAnimals();
  }, [fetchAnimals]);

  const addAnimal = useCallback((animal: Animal) => {
    setAnimals(prev => [...prev, animal]);
  }, []);

  return {
    animals,
    loading,
    error,
    refetch: fetchAnimals,
    addAnimal,
  };
}
